import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
  HttpHeaders
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MainState } from "@app/store/state";
import { PopNotificationsService } from "@core/services/pop-notifications.service";
import { environment } from "@env/environment";
import { Logout } from "@features/account/store/auth.actions";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { RouteMappingService } from "@shared/api-obfuscation/services/route-mapping.service";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";

import { AuthService } from "./auth.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly ignored401Paths = [
    /.*\/api\/v2\/auth\/login\/$/,
    /.*\/api\/v2\/auth\/verify-2fa\/$/,
    /.*\/api\/v2\/auth\/register\/$/,
    /.*\/api\/v2\/auth\/activate\/$/,
    /.*\/api\/v2\/auth\/resend-activation\/$/,
    /.*\/api\/v2\/auth\/password-reset\/$/,
    /.*\/api\/v2\/auth\/password-reset\/confirm\/$/,
    /.*\/api\/v2\/auth\/password-reset\/validate-token\/$/,
    /.*\/api\/v2\/auth\/resend-2fa-code\/$/,
    /.*\/api\/v2\/auth\/2fa-status\/$/,
    /.*\/api\/v2\/auth\/logout\/$/
  ];

  constructor(
    public readonly store$: Store<MainState>,
    public readonly popNotificationsService: PopNotificationsService,
    public readonly authService: AuthService,
    public readonly translate: TranslateService,
    public readonly routeMappingService: RouteMappingService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let authToken: string;
    let authScheme: string;

    if (request.url.startsWith(environment.classicApiUrl)) {
      authToken = this.authService.getClassicApiToken();
      authScheme = "Token";
    }

    const headers = {};

    // Copy original headers.
    for (const key of request.headers.keys()) {
      headers[key] = request.headers.get(key);
    }

    // Set some default if missing.
    for (const setIfMissing of [
      {
        key: "Content-Type",
        value: "application/json; charset=utf-8"
      },
      {
        key: "Accept",
        value: "application/json"
      }
    ]) {
      if (!headers[setIfMissing.key]) {
        headers[setIfMissing.key] = setIfMissing.value;
      } else if (headers[setIfMissing.key] === "__unset__") {
        delete headers[setIfMissing.key];
      }
    }

    // Skip Authorization header for auth endpoints
    const isAuthEndpoint =
      request.url.includes("/auth/login") ||
      request.url.includes("/auth/verify-2fa/") ||
      request.url.includes("/auth/register") ||
      request.url.includes("/auth/activate") ||
      request.url.includes("/auth/resend-activation");

    // Set the Authorization header only for non-auth endpoints
    if (authToken && !isAuthEndpoint) {
      headers["Authorization"] = `${authScheme} ${authToken}`;
    }

    // Always include credentials for cookie handling
    request = request.clone({
      headers: new HttpHeaders(headers),
      withCredentials: true
    });

    return next.handle(request).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          const shouldIgnore = this.shouldIgnore401Error(error.url);

          // Don't dispatch logout for "Invalid token" errors - let ServerErrorsInterceptor handle it
          const isInvalidTokenError = error.error?.detail === "Invalid token.";

          if (!shouldIgnore && !isInvalidTokenError) {
            this.store$.dispatch(new Logout());

            const invalidSessionMessage = this.translate.instant("Your session is invalid, please log in again");
            const login = this.translate.instant("Log in");
            const openLink = `<a href="/account/login" class="d-inline-block mt-3 btn btn-primary">`;
            const closeLink = `</a>`;
            const message = `${invalidSessionMessage}<br/>${openLink}${login}${closeLink}`;
            this.popNotificationsService.warning(message, null, { enableHtml: true, disableTimeOut: true });
          }
        }

        return throwError(error);
      })
    );
  }

  /**
   * Determines if a 401 error should be ignored (not trigger logout)
   * @param url The URL that returned the 401 error
   * @returns true if the error should be ignored, false otherwise
   */
  shouldIgnore401Error(url: string): boolean {
    // Check if URL matches any of the ignored auth endpoint patterns
    let shouldIgnore = this.ignored401Paths.some(regex => regex.test(url));

    // If not matched yet, check if this is an obfuscated auth URL
    if (!shouldIgnore) {
      // Get all obfuscated URLs for auth endpoints
      const obfuscatedAuthUrls: string[] = [];
      for (const pattern of this.ignored401Paths) {
        const obfuscatedUrls = this.routeMappingService.getObfuscatedUrlsForPattern(pattern);
        obfuscatedAuthUrls.push(...obfuscatedUrls);
      }

      // Check if the error URL matches any obfuscated auth URL
      shouldIgnore = obfuscatedAuthUrls.some(obfuscatedUrl => {
        // Extract path from url if needed
        let errorPath = url;
        try {
          const urlObj = new URL(url);
          errorPath = urlObj.pathname;
        } catch {
          // Not a valid URL, assume it's already a path
        }
        return errorPath.includes(obfuscatedUrl);
      });
    }

    return shouldIgnore;
  }
}
